import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { validateCreditsForAction } from '#creditFeature/api/creditsValidation';
import { useError } from '#globalContexts/ErrorContext';
import { GERMAN_STATE_LABELS } from '#lib/constants/germanStates';

import { useInvokeUnifiedSearch } from '../models/prospectQueries/mutations';
import { resolveIndustryOptions } from '../models/prospectQueries/queries';
import { buildSearchParameters } from '../utils/buildSearchParameters';
import { validateSearchFilters } from '../utils/validateSearchFilters';

// Default filter state
export const DEFAULT_FILTERS = {
	industrySearchTerms: '',
	industryCodes: [],
	location: '',
	locationMode: 'place', // 'place' (Ort + Umkreis) | 'state' (Bundesland) | 'plz' (PLZ range + list)
	plzFrom: '',
	plzTo: '',
	plzList: '', // free-text: exact codes, ranges, prefix wildcards ('38124, 38126, 39*')
	states: [], // Shared (Maps + HR), Germany only, state mode only
	country: 'Deutschland',
	radius: 25,
	maxResults: 25,
	websiteFilter: 'with',
	// Shared (Maps + HR)
	excludeKeywords: '',
	// Maps-specific rating filters
	ratingFrom: '',
	ratingTo: '',
	includeClosedPlaces: false,
	minReviewCount: '',
	onlyNameMatches: false,
	// HR-specific
	searchKeywords: '',
	legalForm: [],
	revenueFrom: '',
	revenueTo: '',
	balanceTotalFrom: '',
	balanceTotalTo: '',
	earningsFrom: '',
	earningsTo: '',
	employeesFrom: '',
	employeesTo: '',
	includeEstimated: true,
	eventType: '',
	eventDateFrom: '',
	eventDateTo: '',
	excludeProspectQueryIds: [],
	excludeContactListIds: [],
};

// HR-specific filter defaults for resetting
const HR_FILTER_DEFAULTS = {
	searchKeywords: '',
	legalForm: DEFAULT_FILTERS.legalForm,
	industryCodes: [],
	revenueFrom: '',
	revenueTo: '',
	balanceTotalFrom: '',
	balanceTotalTo: '',
	earningsFrom: '',
	earningsTo: '',
	employeesFrom: '',
	employeesTo: '',
	includeEstimated: true,
	eventType: '',
	eventDateFrom: '',
	eventDateTo: '',
	excludeProspectQueryIds: [],
	excludeContactListIds: [],
};

// Maps-specific filter defaults for resetting
const MAPS_FILTER_DEFAULTS = {
	ratingFrom: '',
	ratingTo: '',
	includeClosedPlaces: false,
	minReviewCount: '',
	onlyNameMatches: false,
};

// Fields that belong to HR or Maps exclusively
const HR_FILTER_FIELDS = [
	'searchKeywords',
	'revenueFrom',
	'revenueTo',
	'balanceTotalFrom',
	'balanceTotalTo',
	'earningsFrom',
	'earningsTo',
	'employeesFrom',
	'employeesTo',
	'includeEstimated',
	'eventType',
	'eventDateFrom',
	'eventDateTo',
	'legalForm',
	'industryCodes',
	'excludeProspectQueryIds',
	'excludeContactListIds',
];
const MAPS_FILTER_FIELDS = ['ratingFrom', 'ratingTo', 'includeClosedPlaces', 'minReviewCount', 'onlyNameMatches'];

/**
 * Hook for managing unified search filters, search type determination, and search execution.
 * Manages state for Maps- and HR-specific filters, automatically resolves conflicts between
 * filter groups, and validates inputs before search execution.
 *
 * @param {Object} options
 * @param {Function} options.handleSearchQuerySelect - Callback when a new search query is created (receives prospectQueryId)
 * @returns {Object} Hook return value
 * @returns {Object} returns.filters - Current filter state (DEFAULT_FILTERS shape)
 * @returns {"maps"|"hr"} returns.searchType - Resolved search type
 * @returns {boolean} returns.isGermany - Whether the country filter is set to Germany
 * @returns {boolean} returns.hasActiveHRFilters - Whether HR-specific filters are active
 * @returns {boolean} returns.hasActiveMapsFilters - Whether Maps-specific filters are active
 * @returns {Function} returns.handleFilterChange - Handler for filter changes (filterType, value)
 * @returns {Function} returns.handleStartSearch - Starts the search with current filters
 * @returns {Function} returns.handleSearchTypeToggle - Manually toggles between Maps and HR
 */
export const useSearchFilters = ({ handleSearchQuerySelect }) => {
	const invokeSearch = useInvokeUnifiedSearch();
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useError();

	const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
	const [searchTypeOverride, setSearchTypeOverride] = useState(null);

	// Check if any HR-specific filter has a non-default value
	const hasActiveHRFilters = () => {
		const hasFinanceFilter =
			filters.revenueFrom ||
			filters.revenueTo ||
			filters.balanceTotalFrom ||
			filters.balanceTotalTo ||
			filters.earningsFrom ||
			filters.earningsTo ||
			filters.employeesFrom ||
			filters.employeesTo;
		const hasEventFilter = Boolean(filters.eventType);
		const hasNonDefaultLegalForm =
			JSON.stringify([...filters.legalForm].sort()) !== JSON.stringify([...DEFAULT_FILTERS.legalForm].sort());
		const hasWzCodes = Array.isArray(filters.industryCodes) && filters.industryCodes.length > 0;
		const hasExclusions = filters.excludeProspectQueryIds?.length > 0 || filters.excludeContactListIds?.length > 0;
		return Boolean(hasFinanceFilter || hasEventFilter || hasNonDefaultLegalForm || hasWzCodes || hasExclusions);
	};

	// Check if any Maps-specific filter has a non-default value
	const hasActiveMapsFilters = () => {
		return Boolean(
			filters.ratingFrom ||
			filters.ratingTo ||
			filters.includeClosedPlaces ||
			filters.minReviewCount ||
			filters.onlyNameMatches,
		);
	};

	const isGermany = filters.country === 'Deutschland';

	// Determine search type: manual override takes precedence, otherwise auto-detect
	const resolveSearchType = () => {
		if (searchTypeOverride) return searchTypeOverride;
		return isGermany && hasActiveHRFilters() ? 'hr' : 'maps';
	};

	// Toggle search type manually and clear conflicting filters
	const handleSearchTypeToggle = () => {
		const currentType = resolveSearchType();
		if (currentType === 'maps') {
			if (!isGermany) return;
			if (hasActiveMapsFilters()) {
				setFilters((prev) => ({ ...prev, ...MAPS_FILTER_DEFAULTS, websiteFilter: 'all' }));
			} else {
				setFilters((prev) => ({ ...prev, websiteFilter: 'all' }));
			}
			setSearchTypeOverride('hr');
		} else {
			if (hasActiveHRFilters()) {
				setFilters((prev) => ({ ...prev, ...HR_FILTER_DEFAULTS, websiteFilter: 'with' }));
			} else {
				setFilters((prev) => ({ ...prev, websiteFilter: 'with' }));
			}
			setSearchTypeOverride('maps');
		}
	};

	// Generate query name
	const generateQueryName = (firstTerm, location) => {
		const today = new Date();
		const day = String(today.getDate()).padStart(2, '0');
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const year = today.getFullYear();
		return `${day}.${month}.${year} - ${firstTerm || 'Suche'}${location ? ` - ${location}` : ''}`;
	};

	// Build search parameters from current filter state (delegates to shared utility)

	// Unified search handler
	const handleStartSearch = async () => {
		const searchType = resolveSearchType();

		const validationError = validateSearchFilters(filters, searchType);
		if (validationError) {
			showError(validationError);
			return;
		}

		// Validate credits for Maps search
		if (searchType === 'maps') {
			try {
				const creditValidation = await validateCreditsForAction('googleMapsSearch', filters.maxResults);
				if (!creditValidation.hasEnoughCredits) {
					showError({ title: 'Nicht genügend Credits', message: creditValidation.error });
					return;
				}
			} catch {
				showError({ title: 'Es ist ein Fehler aufgetreten', message: 'Fehler beim Prüfen der Credits' });
				return;
			}
		}

		const searchTerms = (filters.industrySearchTerms || '')
			.split(',')
			.map((x) => x.trim())
			.filter(Boolean);

		// Prefer manually picked code for the query label, fall back to free-text
		const queryLabelSource = filters.industryCodes?.[0] || searchTerms[0] || 'Filter-Suche';

		let locationLabel = filters.location || filters.country || '';
		if (filters.locationMode === 'plz') {
			if (filters.plzFrom && filters.plzTo) locationLabel = `PLZ ${filters.plzFrom}–${filters.plzTo}`;
			else if (filters.plzList?.trim()) locationLabel = `PLZ ${filters.plzList.trim()}`;
			else locationLabel = 'PLZ';
		} else if (filters.locationMode === 'state') {
			locationLabel = filters.states.map((s) => GERMAN_STATE_LABELS[s] || s).join(', ');
		}
		const queryName = generateQueryName(queryLabelSource.substring(0, 15), locationLabel);

		// Resolve free-text terms to explicit WZ codes so the worker skips the AI
		// roundtrip; usually a cache hit because the preview resolved them already.
		let resolvedIndustry = null;
		const hasExplicitCodes = Array.isArray(filters.industryCodes) && filters.industryCodes.length > 0;
		if (searchType === 'hr' && !hasExplicitCodes && searchTerms.length > 0) {
			try {
				resolvedIndustry = await queryClient.fetchQuery(resolveIndustryOptions(searchTerms));
			} catch {
				// Non-fatal: the worker resolves industrySearchTerms itself as fallback.
				resolvedIndustry = null;
			}
		}

		const searchParameters = buildSearchParameters(filters, searchType, searchTerms, resolvedIndustry);

		try {
			const response = await invokeSearch.mutateAsync({
				queryName,
				searchType,
				searchParameters,
			});

			const actualType = response.searchType || searchType;
			showSuccess({
				title: 'Suche gestartet',
				message:
					actualType === 'maps' ? 'Karten-Suche wurde gestartet.' : 'Handelsregister-Suche wurde gestartet.',
			});

			if (response?.prospectQueryId) {
				handleSearchQuerySelect(response.prospectQueryId);
			}
		} catch (err) {
			showError({ title: 'Fehler bei der Suche', error: err });
		}
	};

	// Filter change handler
	const handleFilterChange = (filterType, value) => {
		if (filterType === 'reset') {
			setFilters({ ...DEFAULT_FILTERS });
			setSearchTypeOverride(null);
			return;
		}

		// Reset manual override when user changes a type-specific filter
		if (HR_FILTER_FIELDS.includes(filterType) || MAPS_FILTER_FIELDS.includes(filterType)) {
			setSearchTypeOverride(null);
		}

		// When switching away from Deutschland, clear HR filters and override
		if (filterType === 'country' && value !== 'Deutschland' && filters.country === 'Deutschland') {
			if (hasActiveHRFilters()) {
				showSuccess({
					title: 'Handelsregister-Filter zurückgesetzt',
					message:
						'Die Handelsregister-Filter (Finanzen, Rechtsform, Ereignisse) sind nur für Deutschland verfügbar und wurden zurückgesetzt.',
				});
			}
			if (searchTypeOverride === 'hr') setSearchTypeOverride(null);
			// PLZ mode and Bundesland are Germany-only
			setFilters((prev) => ({
				...prev,
				country: value,
				...HR_FILTER_DEFAULTS,
				locationMode: 'place',
				plzFrom: '',
				plzTo: '',
				plzList: '',
				states: [],
			}));
			return;
		}

		// Auto-reset conflicting filter group
		if (HR_FILTER_FIELDS.includes(filterType)) {
			// industryCodes array is replaced (from WzCodePickerModal)
			if (filterType === 'industryCodes') {
				const codes = Array.isArray(value) ? value : [];
				const isActivatingHR = codes.length > 0;
				if (hasActiveMapsFilters()) {
					showSuccess({
						title: 'Bewertungs-Filter zurückgesetzt',
						message:
							'Bewertungs-Filter (Sterne, Reviews) sind nur für die Karten-Suche verfügbar und wurden zurückgesetzt.',
					});
					setFilters((prev) => ({
						...prev,
						industryCodes: codes,
						...MAPS_FILTER_DEFAULTS,
						...(isActivatingHR ? { websiteFilter: 'all' } : {}),
					}));
				} else {
					setFilters((prev) => ({
						...prev,
						industryCodes: codes,
						...(isActivatingHR ? { websiteFilter: 'all' } : {}),
					}));
				}
				return;
			}
			// Array filters (e.g. legalForm) need toggle logic, not direct assignment
			if (Array.isArray(DEFAULT_FILTERS[filterType])) {
				setFilters((prev) => {
					const currentValues = prev[filterType] || [];
					const newValues = currentValues.includes(value)
						? currentValues.filter((v) => v !== value)
						: [...currentValues, value];
					const isActivatingHR = newValues.length > 0;
					return { ...prev, [filterType]: newValues, ...(isActivatingHR ? { websiteFilter: 'all' } : {}) };
				});
				return;
			}
			// Scalar HR filters
			const isActivatingHR = Array.isArray(value) ? value.length > 0 : Boolean(value);
			if (hasActiveMapsFilters()) {
				showSuccess({
					title: 'Bewertungs-Filter zurückgesetzt',
					message:
						'Bewertungs-Filter (Sterne, Reviews) sind nur für die Karten-Suche verfügbar und wurden zurückgesetzt.',
				});
				setFilters((prev) => ({
					...prev,
					[filterType]: value,
					...MAPS_FILTER_DEFAULTS,
					...(isActivatingHR ? { websiteFilter: 'all' } : {}),
				}));
			} else {
				setFilters((prev) => ({
					...prev,
					[filterType]: value,
					...(isActivatingHR ? { websiteFilter: 'all' } : {}),
				}));
			}
			return;
		}
		if (MAPS_FILTER_FIELDS.includes(filterType) && hasActiveHRFilters()) {
			showSuccess({
				title: 'Handelsregister-Filter zurückgesetzt',
				message:
					'HR-Filter (Finanzen, Rechtsform, Ereignisse) sind nur für die Handelsregister-Suche verfügbar und wurden zurückgesetzt.',
			});
			setFilters((prev) => ({ ...prev, [filterType]: value, ...HR_FILTER_DEFAULTS, websiteFilter: 'with' }));
			return;
		}

		// Check if it's a direct (non-array) field
		if (filterType in DEFAULT_FILTERS && !Array.isArray(DEFAULT_FILTERS[filterType])) {
			setFilters((prev) => ({ ...prev, [filterType]: value }));
			return;
		}

		// Toggle for array filters (legalForm)
		setFilters((prev) => {
			const currentValues = prev[filterType] || [];
			const newValues = currentValues.includes(value)
				? currentValues.filter((v) => v !== value)
				: [...currentValues, value];

			if (HR_FILTER_FIELDS.includes(filterType)) {
				const isActivatingHR = newValues.length > 0;
				if (hasActiveMapsFilters()) {
					return {
						...prev,
						[filterType]: newValues,
						...MAPS_FILTER_DEFAULTS,
						...(isActivatingHR ? { websiteFilter: 'all' } : {}),
					};
				}
				return { ...prev, [filterType]: newValues, ...(isActivatingHR ? { websiteFilter: 'all' } : {}) };
			}
			return { ...prev, [filterType]: newValues };
		});
	};

	return {
		filters,
		searchType: resolveSearchType(),
		isGermany,
		hasActiveHRFilters: hasActiveHRFilters(),
		hasActiveMapsFilters: hasActiveMapsFilters(),
		handleFilterChange,
		handleStartSearch,
		handleSearchTypeToggle,
	};
};
